import Servers from '@/services/servers';
import Assets from '@/services/assets';
import { workload as Workload } from '@/lib/workload';
import { dateFormatHMS } from '@/lib/dateFormat.js';
import { totalNumber } from '@/lib/calculations.js';
import { dateFormat, runningTime } from '@/lib/dateFormat';
import Earnings from '@/services/earnings';

const state = {
    servers: [],
    earnings: [],
    allServers: [],
    scenarioIds: [],
    filteredServers: [],
    allGamesData: [],
    currentPlayers: 0,
    peakPlayers: 0,
    maxPlayers: 0,
    workloadServer: 0,
    workloadPeak: 0,
    loading: false,
    allServersCount: 0,
    totalCurrentPlayers: 0,
};

const getters = {
    getServers: state => state.servers,
    getFilteredServers: state => state.filteredServers,
    getAllServers: state => state.allServers,
    getCurrentPlayers: state => state.currentPlayers,
    getPeakPlayers: state => state.peakPlayers,
    getMaxPlayers: state => state.maxPlayers,
    getWorkloadServer: state => state.workloadServer,
    getWorkloadPeak: state => state.workloadPeak,
    getLoading: state => state.loading,
    getGames: state => state.allGamesData,
    scenarioIds: state => state.scenarioIds,
    allServersCount: state => state.allServersCount,
    totalCurrentPlayers: state => state.totalCurrentPlayers,
};

const actions = {
    async fetchServers({ commit }) {
        commit('setLoading', true);
        const response = await Servers.getAll();
        const earningsData = await Earnings.getAll();
        commit('resetServerData');
        commit('setServers', response.data);
        commit('setEarnings', earningsData.data);
        commit('allSevers');
        commit('countCurrentPlayers');
        commit('countPeakPlayers');
        commit('countMaxPlayers');
        commit('workload');
        commit('gamesIds');
        const data = await allGamesData(state);
        commit('allGamesData', data);
        commit('allServersCount');
        commit('totalCurrentPlayers');
        commit('setFilteredServers');
        commit('setLoading', false);
    },
};

const mutations = {
    resetServerData(state) {
        state.servers = [];
        state.allGamesData = null;
        state.allServers = [];
        state.currentPlayers = 0;
        state.peakPlayers = 0;
        state.maxPlayers = 0;
        state.workloadServer = 0;
        state.workloadPeak = 0;
        state.scenarioIds = [];
        state.allGamesData = null;
        state.filteredServers = [];
    },
    setServers: (state, servers) => (state.servers = servers),
    setEarnings: (state, earnings) => (state.earnings = earnings),

    allSevers(state) {
        state.servers.providerServerUsageStats.forEach(element =>
            state.allServers.push(element)
        );
        state.servers.rentedServerUsageStats.forEach(element =>
            state.allServers.push(element)
        );
    },
    countCurrentPlayers(state) {
        state.servers.providerServerUsageStats.forEach(
            element => (state.currentPlayers += element.currentPlayers)
        );
        state.servers.rentedServerUsageStats.forEach(
            element => (state.currentPlayers += element.currentPlayers)
        );
    },
    countPeakPlayers(state) {
        state.servers.providerServerUsageStats.forEach(
            element => (state.peakPlayers += element.peakPlayers)
        );
        state.servers.rentedServerUsageStats.forEach(
            element => (state.peakPlayers += element.peakPlayers)
        );
    },
    countMaxPlayers(state) {
        state.servers.providerServerUsageStats.forEach(
            element => (state.maxPlayers += element.playerLimit)
        );
        state.servers.rentedServerUsageStats.forEach(
            element => (state.maxPlayers += element.playerLimit)
        );
    },
    workload(state) {
        state.workloadServer = Workload(state.currentPlayers, state.maxPlayers);
        state.workloadPeak = Workload(state.peakPlayers, state.maxPlayers);
    },
    gamesIds(state) {
        state.servers.providerServerUsageStats.forEach(element =>
            state.scenarioIds.push(element.scenarioId)
        );
        state.servers.rentedServerUsageStats.forEach(element =>
            state.scenarioIds.push(element.scenarioId)
        );
    },
    allGamesData: (state, data) => (state.allGamesData = data),
    setLoading: (state, loading) => (state.loading = loading),
    allServersCount(state) {
        if (state.allGamesData.length > 0) {
            state.allServersCount = totalNumber(
                state.allGamesData.filter(game => game.contentType === 'game'),
                'allServersCount'
            );
        }
    },
    totalCurrentPlayers(state) {
        if (state.allGamesData.length > 0) {
            state.totalCurrentPlayers = totalNumber(
                state.allGamesData.filter(game => game.contentType === 'game'),
                'currentPlayers'
            );
        }
    },
    setFilteredServers(state) {
        state.allServers.forEach(obj => {
            let newObj = {
                type: obj.performanceTier,
                provider: 'Ylands',
                gameName: obj.gameName,
                players: `${obj.currentPlayers}` + '/' + `${obj.playerLimit}`,
                peak: `${obj.peakPlayers}` + '/' + `${obj.playerLimit}`,
                expiration: dateFormat(obj.expirationTime),
                runningTime: obj.activeFrom
                    ? runningTime(obj.activeFrom)
                    : 'Inactive',
            };
            state.filteredServers.push(newObj);
        });
        return state.filteredServers;
    },
};

async function allGamesData(state) {
    let gameData = [];
    // Filter unique GUIDs
    const uniqueIds = Array.from(new Set(state.scenarioIds));
    // Get data for individual asset and connect with server data
    for (let index = 0; index < uniqueIds.length; index++) {
        let gameId = state.scenarioIds[index];
        let data = await byGames(gameId);
        if (data) {
            // Find in allServers - server with the same GUID.
            let servers = state.allServers.filter(
                server =>
                    server.scenarioId ===
                    (data.metadata.asset_guid || data.metadata.guid)
            );
            let allServersCount = servers.length;

            // Get earnings for each of the game
            let earning = state.earnings.earnings.scenario.filter(
                earning =>
                    earning.scenarioId ===
                    (data.metadata.asset_guid || data.metadata.guid)
            );

            // Count only active servers
            let activeServers = 0;
            let activeServersLength = servers.filter(
                server => server.active === true
            );
            activeServers += activeServersLength.length;

            let wrkLoadServer = 0;
            let wrkLoadPeak = 0;
            let serverExpiration = [];
            let playerLimit = 0;
            let currentPlayers = 0;
            let peakPlayers = 0;
            let contentType;
            let scenarioEarning = 0;

      
            switch (data.content_type) {
                case 2:
                    contentType = 'creator-blueprint';
                    break;
                case 3:
                    contentType = 'game';
                    break;
                case 4:
                    contentType = 'game-blueprint';
                    break;
            }      


            // Collect all necessary data
            for (let index = 0; index < servers.length; index++) {
                currentPlayers += servers[index].currentPlayers;
                playerLimit += servers[index].playerLimit;
                peakPlayers += servers[index].peakPlayers;
                serverExpiration.push(servers[index].expirationTime);
            }
            wrkLoadServer += Workload(currentPlayers, playerLimit);
            wrkLoadPeak += Workload(peakPlayers, playerLimit);
            if (earning[0]) {
                scenarioEarning += earning[0].earned;
            }

            // Add data to result object
            gameData.push({
                name: data.name,
                activeServers: activeServers,
                wrkLoadServer: wrkLoadServer,
                wrkLoadPeak: wrkLoadPeak,
                serverExpiration: dateFormatHMS(Math.max(serverExpiration)),
                allServersCount: allServersCount,
                currentPlayers: currentPlayers,
                contentType: contentType,
                scenarioEarning: scenarioEarning,
            });
        }
    }
    // return gameData.sort((a, b) =>
    //     a.scenarioEarning > b.scenarioEarning ? 1 : -1
    // );
    return gameData;
}

// Get asset bt GUID
async function byGames(id) {
    let game;
    try {
        game = await Assets.getById(id);
        return game.data;
    } catch (e) {
        console.log(e);
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
